"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import { RefObject, useEffect, useRef, useState } from "react";

interface DesktopControlDemoProps {
  controlRef: RefObject<HTMLDivElement>;
  onDownloadClick?: () => void;
  onShowDownloadChange?: (show: boolean) => void;
}

export function DesktopControlDemo({ controlRef, onDownloadClick, onShowDownloadChange }: DesktopControlDemoProps) {
  const { scrollYProgress } = useScroll({
    target: controlRef,
    offset: ["start center", "end end"],
  });

  // 视频播放状态
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  // 基于滚动进度控制视频显示 - 第二阶段 (0.7-1.0, 占总进度30%)
  const showVideo = useTransform(scrollYProgress, [0.7, 0.75, 0.9, 1.0], [0, 1, 1, 1]);


  // 视频播放控制 - 基于 showVideo motion value
  useEffect(() => {
    const unsubscribe = showVideo.on("change", (value) => {
      const shouldPlay = value > 0;
      
      if (videoRef.current) {
        if (shouldPlay && !videoPlaying) {
          videoRef.current.currentTime = 0;
          videoRef.current.play();
          setVideoPlaying(true);
        } else if (!shouldPlay && videoPlaying) {
          videoRef.current.pause();
          setVideoPlaying(false);
        }
      }
    });

    return unsubscribe;
  }, [showVideo, videoPlaying]);

  // 文本覆盖层控制已转移到父组件


  // 浏览器整体出现动画 (0.1-0.3) - 扩展时间
  const browserOpacity = useTransform(scrollYProgress, [0.1, 0.3], [0, 1]);

  // macOS桌面元素出现动画 - 从左侧滑入 (0.1-0.32) - 扩展时间
  const menuBarAppearX = useTransform(scrollYProgress, [0.1, 0.16], [-200, 0]);
  const menuBarAppearOpacity = useTransform(scrollYProgress, [0.1, 0.16], [0, 1]);

  const window1AppearX = useTransform(scrollYProgress, [0.12, 0.18], [-200, 0]);
  const window1AppearOpacity = useTransform(scrollYProgress, [0.12, 0.18], [0, 1]);

  const window2AppearScale = useTransform(scrollYProgress, [0.3, 0.36], [0.1, 1]);
  const window2AppearOpacity = useTransform(scrollYProgress, [0.3, 0.36], [0, 1]);

  const dockAppearY = useTransform(scrollYProgress, [0.18, 0.24], [100, 0]);
  const dockAppearOpacity = useTransform(scrollYProgress, [0.18, 0.24], [0, 1]);

  const cursorAppearX = useTransform(scrollYProgress, [0.24, 0.3], [-100, 350]);
  const cursorAppearRotation = useTransform(scrollYProgress, [0.24, 0.3], [-45, 0]);
  const cursorAppearOpacity = useTransform(scrollYProgress, [0.24, 0.3], [0, 1]);

  // 计算机控制动画序列 (0.3-0.6) - 扩展时间并调整时机
  // 1. Click 操作 - 点击Dock图标 (0.3-0.34) - 调整时机
  const clickIndicatorOpacity = useTransform(scrollYProgress, [0.3, 0.32, 0.34], [0, 1, 0]);
  const dockIcon1Scale = useTransform(scrollYProgress, [0.3, 0.32, 0.34], [1, 1.3, 1]);
  const cursorClickY = useTransform(scrollYProgress, [0.3, 0.32], [200, 430]);

  // 2. Scroll 操作 - 在窗口内滚动 (0.34-0.38) - 调整时机
  const scrollContentY = useTransform(scrollYProgress, [0.34, 0.38], [0, -50]);
  const cursorScrollY = useTransform(scrollYProgress, [0.34, 0.38], [430, 250]);
  const cursorScrollX = useTransform(scrollYProgress, [0.34, 0.38], [350, 200]);

  // 3. Hotkey 操作 - 显示快捷键 (0.38-0.42) - 调整时机
  const hotkeyIndicatorOpacity = useTransform(scrollYProgress, [0.38, 0.4, 0.42], [0, 1, 0]);

  // 4. Typing 操作 - 在输入框输入 (0.42-0.46) - 调整时机
  const typingTextOpacity = useTransform(scrollYProgress, [0.42, 0.46], [0, 1]);
  const cursorTypeX = useTransform(scrollYProgress, [0.42, 0.44], [200, 140]);
  const cursorTypeY = useTransform(scrollYProgress, [0.42, 0.44], [250, 180]);

  // 5. Press 操作 - 按下按钮 (0.46-0.5) - 调整时机
  const buttonPressScale = useTransform(scrollYProgress, [0.46, 0.48, 0.5], [1, 0.95, 1]);

  // 6. Take Snapshot 操作 - 截图效果 (0.5-0.54) - 调整时机
  const snapshotFlashOpacity = useTransform(scrollYProgress, [0.5, 0.52, 0.54], [0, 0.8, 0]);
  const snapshotFrameOpacity = useTransform(scrollYProgress, [0.5, 0.54, 0.56], [0, 1, 0]);

  // 7. Wait 操作 - 显示加载动画 (0.54-0.58) - 调整时机
  const waitIndicatorOpacity = useTransform(scrollYProgress, [0.54, 0.58, 0.6], [0, 1, 0]);
  const waitSpinnerRotation = useTransform(scrollYProgress, [0.54, 0.58], [0, 360]);

  // 8. Move Window 操作 - 移动窗口 (0.58-0.62) - 调整时机
  const window1MoveX = useTransform(scrollYProgress, [0.58, 0.62], [0, 150]);
  const window1MoveY = useTransform(scrollYProgress, [0.58, 0.62], [0, -80]);
  const cursorMoveX = useTransform(scrollYProgress, [0.58, 0.62], [140, 290]);
  const cursorMoveY = useTransform(scrollYProgress, [0.58, 0.62], [180, 100]);

  // 分层右侧退场动画 (0.62-0.7) - 改为右移退场，菜单栏不退出
  // 1. Dock右侧退场 (0.62-0.64)
  const dockExitX = useTransform(scrollYProgress, [0.62, 0.64], [0, 200]);
  const dockExitOpacity = useTransform(scrollYProgress, [0.62, 0.64], [1, 0]);

  // 2. 窗口2右侧退场 (0.64-0.66)
  const window2ExitX = useTransform(scrollYProgress, [0.64, 0.66], [0, 200]);
  const window2ExitOpacity = useTransform(scrollYProgress, [0.64, 0.66], [1, 0]);

  // 3. 窗口1右侧退场 (0.66-0.68)
  const window1ExitX = useTransform(scrollYProgress, [0.66, 0.68], [0, 200]);
  const window1ExitOpacity = useTransform(scrollYProgress, [0.66, 0.68], [1, 0]);

  // 4. 鼠标右侧退场 (0.68-0.7)
  const cursorExitX = useTransform(scrollYProgress, [0.68, 0.7], [0, 200]);
  const cursorExitOpacity = useTransform(scrollYProgress, [0.68, 0.7], [1, 0]);

  // 菜单栏保持不动（不退出）
  const menuBarExitX = useTransform(scrollYProgress, [1, 1], [0, 0]);
  const menuBarExitOpacity = useTransform(scrollYProgress, [1, 1], [1, 1]);

  // 组合透明度变换
  const combinedMenuBarOpacity = useTransform([menuBarAppearOpacity, menuBarExitOpacity], ([appear, exit]) => Math.min(appear as number, exit as number));
  const combinedWindow1Opacity = useTransform([window1AppearOpacity, window1ExitOpacity], ([appear, exit]) => Math.min(appear as number, exit as number));
  const combinedWindow2Opacity = useTransform([window2AppearOpacity, window2ExitOpacity], ([appear, exit]) => Math.min(appear as number, exit as number));
  const combinedDockOpacity = useTransform([dockAppearOpacity, dockExitOpacity], ([appear, exit]) => Math.min(appear as number, exit as number));
  const combinedCursorOpacity = useTransform([cursorAppearOpacity, cursorExitOpacity], ([appear, exit]) => Math.min(appear as number, exit as number));

  // Dock图标配置
  const dockIcons = [
    { id: 1, color: "bg-white/10 border border-white/10" },
    { id: 2, color: "bg-white/10 border border-white/10" },
    { id: 3, color: "bg-white/10 border border-white/10" },
    { id: 4, color: "bg-white/10 border border-white/10" },
    { id: 5, color: "bg-white/10 border border-white/10" },
    { id: 6, color: "bg-white/10 border border-white/10" },
    { id: 7, color: "bg-white/10 border border-white/10" },
    { id: 8, color: "bg-white/10 border border-white/10" },
  ];

  return (
    <div className="w-full relative flex items-center justify-center">
        {/* 视频容器 - 基于滚动进度显示 (第二阶段) */}
        <motion.div
          className="absolute inset-0 z-20 flex items-center justify-center"
          style={{ 
            opacity: showVideo,
            x: useTransform(showVideo, [0, 1], [-100, 0]),
            filter: useTransform(showVideo, [0, 1], ["blur(4px)", "blur(0px)"])
          }}
        >
          <video
            ref={videoRef}
            className="w-full h-full object-cover rounded-2xl"
            src="https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/Website+main+page+animation/Computer+USE.webm"
            autoPlay
            loop
            muted
            playsInline
          />
        </motion.div>

        {/* Wireframe容器 */}
        <motion.div
          className="w-[700px] h-[500px] border-2 border-white/10 rounded-lg bg-white/10 relative overflow-hidden"
          style={{ 
            opacity: useTransform(showVideo, [0, 0.1, 0.5, 1], [1, 1, 0.3, 0])
          }}
        >
          {/* macOS Menu Bar */}
          <motion.div
            className="absolute top-0 left-0 right-0 h-6 bg-white/10 border-b border-white/10"
            style={{
              x: useTransform([menuBarAppearX, menuBarExitX], ([appear, exit]) => (appear as number) + (exit as number)),
              opacity: combinedMenuBarOpacity,
            }}
          >
            <div className="flex items-center justify-between h-full px-3">
              <div className="flex items-center gap-4">
                <div className="w-4 h-4 bg-white/80 rounded"></div>
                <div className="flex gap-3">
                  <div className="h-2 bg-white/60 rounded w-6"></div>
                  <div className="h-2 bg-white/60 rounded w-8"></div>
                  <div className="h-2 bg-white/60 rounded w-7"></div>
                  <div className="h-2 bg-white/60 rounded w-10"></div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-white/20 rounded"></div>
                <div className="w-3 h-3 bg-white/20 rounded"></div>
                <div className="h-2 bg-white/60 rounded w-12"></div>
              </div>
            </div>
          </motion.div>

          {/* Desktop Background */}
          <div className="absolute inset-0 bg-white/5"></div>

          {/* Window 1 - Main Application */}
          <motion.div
            className="absolute w-96 h-64 bg-white/10 rounded-lg shadow-2xl overflow-hidden border border-white/10"
            style={{
              x: useTransform(() => {
                const appearX = window1AppearX.get();
                const moveX = window1MoveX.get();
                const exitX = window1ExitX.get();
                const baseX = 150; // 居中位置
                return baseX + appearX + moveX + exitX;
              }),
              y: useTransform(() => {
                const moveY = window1MoveY.get();
                const basePosY = 80; // 稍微向下移动
                return basePosY + moveY;
              }),
              opacity: combinedWindow1Opacity,
            }}
          >
            {/* Window Header */}
            <div className="h-8 bg-white/10 rounded-t-lg flex items-center px-3 border-b border-white/10">
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-white/10 border border-white/10"></div>
                <div className="w-3 h-3 rounded-full bg-white/10 border border-white/10"></div>
                <div className="w-3 h-3 rounded-full bg-white/10 border border-white/10"></div>
              </div>
              <div className="flex-1 text-center">
                <div className="h-2 bg-white/40 rounded w-12 mx-auto"></div>
              </div>
            </div>

            {/* Window Content */}
            <motion.div className="p-4 space-y-3 overflow-hidden">
              <motion.div style={{ y: scrollContentY }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-white/10 rounded border border-white/10"></div>
                  <div className="h-4 bg-white/10 rounded w-2/3"></div>
                </div>

                {/* Input field for typing */}
                <div className="h-8 bg-white/5 border border-white/10 rounded px-3 flex items-center">
                  <motion.div
                    className="h-2 bg-white/10 rounded"
                    style={{
                      width: useTransform(typingTextOpacity, [0, 1], ["0%", "40%"]),
                    }}
                  />
                </div>

                {/* Button for press */}
                <motion.div
                  className="mt-4 w-24 h-8 bg-white/10 border border-white/10 rounded flex items-center justify-center"
                  style={{ scale: buttonPressScale }}
                >
                  <div className="h-2 bg-white/10 rounded w-16"></div>
                </motion.div>

                {/* List items */}
                <div className="space-y-2 mt-4">
                  <div className="h-3 bg-white/20 rounded w-full"></div>
                  <div className="h-3 bg-white/20 rounded w-5/6"></div>
                  <div className="h-3 bg-white/20 rounded w-4/5"></div>
                  <div className="h-3 bg-white/20 rounded w-3/4"></div>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Window 2 - Secondary Application */}
          <motion.div
            className="absolute w-64 h-48 bg-white/10 rounded-lg shadow-xl border border-white/10"
            style={{
              x: useTransform(() => {
                const exitX = window2ExitX.get();
                const baseX = 220; // 最终位置，稍微偏右
                return baseX + exitX;
              }),
              y: 240, // 固定位置
              scale: window2AppearScale,
              opacity: combinedWindow2Opacity,
              transformOrigin: "350px 430px", // 从紫色 app 位置开始缩放
            }}
          >
            {/* Window Header */}
            <div className="h-6 bg-white/10 rounded-t-lg flex items-center px-2 border-b border-white/10">
              <div className="flex gap-1">
                <div className="w-2 h-2 rounded-full bg-white/10 border border-white/10"></div>
                <div className="w-2 h-2 rounded-full bg-white/10 border border-white/10"></div>
                <div className="w-2 h-2 rounded-full bg-white/10 border border-white/10"></div>
              </div>
              <div className="flex-1 text-center">
                <div className="h-1.5 bg-white/40 rounded w-10 mx-auto"></div>
              </div>
            </div>

            {/* Terminal Content */}
            <div className="p-3 space-y-1">
              <div className="h-2 bg-white/10 rounded w-20"></div>
              <div className="h-2 bg-white/10 rounded w-16"></div>
              <div className="h-2 bg-white/10 rounded w-24"></div>
            </div>
          </motion.div>

          {/* macOS Dock */}
          <motion.div
            className="absolute bottom-2 left-1/2 transform -translate-x-1/2 h-16 bg-white/10 rounded-2xl px-3 flex items-center gap-2 border border-white/10"
            style={{
              x: dockExitX,
              y: dockAppearY,
              opacity: combinedDockOpacity,
            }}
          >
            {dockIcons.map((icon, index) => (
              <motion.div
                key={icon.id}
                className={`w-10 h-10 ${icon.color} rounded-xl shadow-lg`}
                style={{
                  scale: index === 4 ? dockIcon1Scale : 1,
                }}
                whileHover={{ scale: 1.2, y: -10 }}
                transition={{ type: "spring", stiffness: 500 }}
              />
            ))}
          </motion.div>

          {/* Mouse Cursor */}
          <motion.div
            className="absolute w-4 h-6 pointer-events-none z-20"
            style={{
              x: useTransform(() => {
                const progress = scrollYProgress.get();
                const exitX = cursorExitX.get();
                let baseX = 0;
                
                if (progress < 0.3) {
                  baseX = cursorAppearX.get();
                } else if (progress < 0.34) {
                  baseX = 350; // Click position
                } else if (progress < 0.38) {
                  baseX = cursorScrollX.get(); // Scroll
                } else if (progress < 0.46) {
                  baseX = cursorTypeX.get(); // Type
                } else if (progress < 0.58) {
                  baseX = 140; // Press/Snapshot/Wait
                } else if (progress < 0.62) {
                  baseX = cursorMoveX.get(); // Move window
                } else {
                  baseX = 290; // Final position
                }
                
                return baseX + exitX;
              }),
              rotate: useTransform(() => {
                const progress = scrollYProgress.get();
                if (progress < 0.3) {
                  return cursorAppearRotation.get();
                } else {
                  return 0;
                }
              }),
              y: useTransform(() => {
                const progress = scrollYProgress.get();
                
                if (progress < 0.3) {
                  return 200;
                } else if (progress < 0.34) {
                  return cursorClickY.get();
                } else if (progress < 0.38) {
                  return cursorScrollY.get();
                } else if (progress < 0.46) {
                  return cursorTypeY.get();
                } else if (progress < 0.58) {
                  return 190;
                } else if (progress < 0.62) {
                  return cursorMoveY.get();
                } else {
                  return 130;
                }
              }),
              scale: 1,
              opacity: combinedCursorOpacity,
            }}
          >
            <svg viewBox="0 0 16 24" fill="none" className="w-full h-full">
              <path d="M0 0L0 16L5 11L8 16L11 14L8 9L16 9L0 0Z" fill="white" stroke="#000" strokeWidth="0.5" />
            </svg>
          </motion.div>

          {/* Operation Indicators */}

          {/* Click Indicator */}
          <motion.div
            className="absolute w-8 h-8 border-2 border-white/10 rounded-full pointer-events-none z-10"
            style={{
              x: 350,
              y: 430,
              opacity: clickIndicatorOpacity,
              scale: useTransform(clickIndicatorOpacity, [0, 1], [0.5, 1.5]),
            }}
          />

          {/* Hotkey Indicator */}
          <motion.div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white/10 px-4 py-2 rounded-lg border border-white/10"
            style={{ opacity: hotkeyIndicatorOpacity }}
          >
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-white/20 rounded"></div>
              <div className="w-2 h-2 bg-white/60 rounded-full"></div>
              <div className="w-8 h-6 bg-white/20 rounded"></div>
            </div>
          </motion.div>

          {/* Snapshot Flash */}
          <motion.div
            className="absolute inset-0 bg-white pointer-events-none z-30"
            style={{ opacity: snapshotFlashOpacity }}
          />

          {/* Snapshot Frame */}
          <motion.div
            className="absolute inset-4 border-2 border-white/10 rounded pointer-events-none z-25"
            style={{
              opacity: snapshotFrameOpacity,
              borderStyle: "dashed",
            }}
          />

          {/* Wait Indicator */}
          <motion.div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 pointer-events-none z-10"
            style={{ opacity: waitIndicatorOpacity }}
          >
            <motion.div
              className="w-full h-full border-2 border-white/10 border-t-transparent rounded-full"
              style={{ rotate: waitSpinnerRotation }}
            />
          </motion.div>
        </motion.div>
      </div>
  );
}
